/**
 * Display names for the bare keys a printed mech's wear record carries (`PrintedMechState`):
 * `destroyedItems` holds weapon instance keys (`<mount>:<slot>`) and system instance ids
 * (`system:<i>`), and `limitedSystemUsed` is keyed by a Limited cap's pool (the item id) or
 * `core_power`. Resolved against the mech's ACTIVE loadout, the same walk `freezeMounts` /
 * `freezeSystems` mint the keys from, so a key names what the fight destroyed. A key this
 * cannot resolve (a talent-integrated item, a loadout edited since) falls back to itself.
 */
import type { Compendium } from "@lancer-vtt/compendium";
import { CORE_POWER_POOL, composedMounts, type PilotData } from "@lancer-vtt/domain";

function entryName(compendium: Compendium, kind: "weapon" | "system", id: string): string | undefined {
  const name = (compendium.getById(kind, id) as { name?: unknown } | undefined)?.name;
  return typeof name === "string" ? name : undefined;
}

/** The name of the item a destroyed-item or Limited-weapon key names on `mechId`. */
export function printedItemName(args: { pilot: PilotData; mechId: string; key: string; compendium: Compendium }): string {
  const { pilot, mechId, key, compendium } = args;
  const mech = pilot.mechs.find((m) => m.id === mechId);
  const loadout = mech?.loadouts[mech.activeLoadoutIndex];
  if (!loadout) return key;
  const system = /^system:(\d+)$/.exec(key);
  if (system) {
    const id = loadout.systems?.[Number(system[1])];
    return (id && entryName(compendium, "system", id)) || key;
  }
  const slot = /^(\d+):(\d+)$/.exec(key);
  if (slot) {
    const weaponId = composedMounts(loadout, pilot, compendium)[Number(slot[1])]?.slots[Number(slot[2])]?.weaponId;
    return (weaponId && entryName(compendium, "weapon", weaponId)) || key;
  }
  return key;
}

/** The name of a `limitedSystemUsed` slot: Core Power, a system, or the raw key. */
export function limitedSystemName(compendium: Compendium, key: string): string {
  if (key === CORE_POWER_POOL) return "Core Power";
  return entryName(compendium, "system", key) ?? entryName(compendium, "weapon", key) ?? key;
}
